import { useCallback, useEffect, useMemo, useState } from "react";
import type { ApiResponse } from "@entrilia/shared/api/types";
import useFetch from "@entrilia/shared/hooks/useFetch";
import type { ApiReportsApiClient, SystemReportsApiClient, UiReportsApiClient } from "../../../../../api/biApi";
import { default as biApi, default as biClient } from "../../../../../api/biApi";
import type {
  ChangeReportGroupRequest,
  ChangeReportsGroupRequest,
  DeleteReportItem,
  RenameReportRequest,
  ReportAuthorizationInfo,
  ReportInfo,
  ReportUsage,
  SharedWithEntity,
  UpdateReportsAuthorizationRequest,
  ValidateReportNameRequest,
} from "../../../../../api/types/svTypes.ts";
import type { FromReport } from "../reports/components/FromReport.ts";
import { applyGroupSharedWith } from "./applyGroupSharedWith.ts";

export default function useReports<T extends ReportInfo>(
  loadReports: (signal?: AbortSignal) => Promise<ApiResponse<T[]>>,
  reportUsage: ReportUsage
) {
  const [reports, reportsError, { fetch: reloadReports }] = useFetch(loadReports);
  const [allReports, setAllReports] = useState<ReportInfo[]>([]);
  const apiClient = useMemo(
    (): UiReportsApiClient | ApiReportsApiClient | SystemReportsApiClient => getReportsClient(reportUsage),
    [reportUsage]
  );

  useEffect(() => {
    if (reports) {
      // TODO #209
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setAllReports(reports);
    }
  }, [reports]);

  const duplicateReport = useCallback(
    async (report: FromReport, name: string, groupId: string | undefined) => {
      const resp = await apiClient.duplicateReport({
        clientCode: report.clientCode,
        reportId: report.reportId,
        name,
        groupId,
      });
      if (resp.success) {
        await reloadReports();
        return resp.data;
      }
      throw new Error(resp.error?.message);
    },

    [reloadReports, apiClient]
  );

  const deleteReports = useCallback(
    async (reports: DeleteReportItem[]) => {
      const resp = await apiClient.deleteReports(reports);
      if (resp.success) {
        await reloadReports();
        return resp;
      }
      throw new Error(resp.error?.message);
    },
    [reloadReports, apiClient]
  );

  const renameReport = useCallback(
    async (request: RenameReportRequest) => {
      const resp = await apiClient.renameReport(request);
      if (resp.success) {
        await reloadReports();
        return resp.data;
      }
      throw new Error(resp.error?.message);
    },
    [reloadReports, apiClient]
  );

  const changeReportGroup = useCallback(
    async (request: ChangeReportGroupRequest) => {
      const resp = await apiClient.changeReportGroup(request);
      if (resp.success) {
        await reloadReports();
        return resp.data;
      }
      throw new Error(resp.error?.message);
    },
    [reloadReports, apiClient]
  );

  const changeReportsGroup = useCallback(
    async (request: ChangeReportsGroupRequest) => {
      const resp = await apiClient.changeReportsGroup(request);
      if (resp.success) {
        await reloadReports();
        return resp;
      }
      throw new Error(resp.error?.message);
    },
    [reloadReports, apiClient]
  );

  const updateReportsSharing = useCallback(
    async (request: UpdateReportsAuthorizationRequest) => {
      const resp = await apiClient.updateReportsSharing(request);
      if (resp.success) {
        await reloadReports();
        return resp;
      }
      throw new Error(resp.error?.message);
    },
    [reloadReports, apiClient]
  );

  const updateReportAuthorizationInfo = useCallback(
    (reportId: string, info: ReportAuthorizationInfo, inheritFromParent: boolean) => {
      setAllReports((reports) =>
        reports.map((report) =>
          report.reportId === reportId ? { ...report, authorization: info, inheritFromParent } : report
        )
      );
    },
    []
  );

  // Applies a group's new sharing to the reports that inherit from it. Their effective shared-with IS the group's,
  // so we derive the display (icons) from it without a reload. The caller's effective `access` is a server-resolved
  // permission and is intentionally left untouched here.
  const applyGroupSharing = useCallback((groupId: string, sharedWith: SharedWithEntity[]) => {
    setAllReports((reports) => applyGroupSharedWith(reports, groupId, sharedWith));
  }, []);

  const generateApiReport = useCallback(
    async (report: FromReport, name: string) => {
      if (isUiReportsApiClient(apiClient)) {
        const resp = await apiClient.generateApiReport({
          clientCode: report.clientCode,
          reportId: report.reportId,
          name,
          groupId: report.groupId,
        });
        if (resp.success) {
          return resp.data;
        }
        throw new Error(resp.error?.message);
      } else {
        throw new Error("This client does not support generating API reports");
      }
    },

    [apiClient]
  );

  const checkUniqueReportNameByUsage = useCallback(async (request: ValidateReportNameRequest, usage: ReportUsage) => {
    const response = await biApi.validateUniqueReportNameByUsage(request, usage);
    if (response.success) {
      return response.data.nameExists !== true;
    }
    throw new Error(response.error?.message);
  }, []);

  return {
    reports: allReports ?? [],
    ui: {
      error: reportsError ? "Loading reports failed" : undefined,
      loaded: reports !== undefined || reportsError !== undefined,
    },
    actions: {
      duplicateReport,
      deleteReports,
      renameReport,
      reloadReports,
      changeReportGroup,
      changeReportsGroup,
      updateReportsSharing,
      updateReportAuthorizationInfo,
      applyGroupSharing,
      generateApiReport,
      checkUniqueReportNameByUsage,
      validateReport: apiClient.validateReport,
      copyReport: apiClient.copyReport,
    },
  };
}

const isUiReportsApiClient = (client: UiReportsApiClient | ApiReportsApiClient): client is UiReportsApiClient => {
  return "generateApiReport" in client;
};

const getReportsClient = (
  reportUsage: ReportUsage
): UiReportsApiClient | ApiReportsApiClient | SystemReportsApiClient => {
  switch (reportUsage) {
    case "Api":
      return biClient.apiReports;
    case "System":
      return biClient.systemReports;
    case "Ui":
      return biClient.uiReports;
    default:
      throw new Error(`Unsupported report usage: ${reportUsage}`);
  }
};
